import React from "react";
import { cn } from "@/utils/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import type {
  DiagnosticQuestion,
  DiagnosticTradeOption,
} from "../../types/diagnostic";

/** Which trade a question belongs to, when it belongs to only one. */
const TradeBadge: React.FC<{
  tradeOptions: DiagnosticTradeOption[];
  tradeTag: string;
}> = ({ tradeOptions, tradeTag }) => (
  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-slate-700 dark:text-gray-300">
    {tradeOptions.find((o) => o.key === tradeTag)?.label ?? tradeTag}
  </span>
);

/**
 * Question label with its "why we ask this" note. The note is what turns the
 * form into a coaching instrument — it tells the coach what to listen for.
 */
const QuestionHeader: React.FC<{
  /**
   * Position among the questions actually shown, not the authored order —
   * hiding an unselected trade's questions would otherwise leave gaps
   * ("01, 06, 07") that read as skipped work.
   */
  number: number;
  question: DiagnosticQuestion;
  tradeOptions: DiagnosticTradeOption[];
}> = ({ number, question, tradeOptions }) => (
  <>
    <div className="flex items-baseline gap-2.5">
      <span className="w-5 shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
        {String(number).padStart(2, "0")}
      </span>
      <Label className="text-[15px] font-normal leading-snug text-gray-900 dark:text-white">
        {question.text}
      </Label>
      {question.tradeTag && (
        <TradeBadge tradeOptions={tradeOptions} tradeTag={question.tradeTag} />
      )}
    </div>
    {question.helpText && (
      <p className="mt-1.5 text-[13px] leading-relaxed text-gray-500 dark:text-gray-400 sm:ml-8">
        {question.helpText}
      </p>
    )}
  </>
);

export interface QuestionFieldProps {
  isDisabled: boolean;
  /** Fires only when a previously unanswered question gets its first answer. */
  onFirstAnswer: (questionId: number) => void;
  ordinalAnswer: number | undefined;
  /** Position among the visible questions, 1-based. */
  position: number;
  question: DiagnosticQuestion;
  registerRef: (el: HTMLDivElement | null) => void;
  setOrdinal: (questionId: number, choiceId: number) => void;
  setText: (questionId: number, value: string) => void;
  setYesNo: (questionId: number, value: boolean) => void;
  textAnswer: string;
  tradeOptions: DiagnosticTradeOption[];
  yesNoAnswer: boolean | undefined;
}

const cardClass =
  "scroll-mt-24 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800";

export const QuestionField: React.FC<QuestionFieldProps> = ({
  isDisabled,
  onFirstAnswer,
  ordinalAnswer,
  position,
  question,
  registerRef,
  setOrdinal,
  setText,
  setYesNo,
  textAnswer,
  tradeOptions,
  yesNoAnswer,
}) => {
  const header = (
    <QuestionHeader
      number={position}
      question={question}
      tradeOptions={tradeOptions}
    />
  );

  if (question.type === "ordinal_mc") {
    const sortedChoices = [...(question.choices ?? [])].sort(
      (a, b) => a.orderNumber - b.orderNumber,
    );
    const answered = ordinalAnswer !== undefined;

    return (
      <div className={cardClass} ref={registerRef}>
        {header}
        <RadioGroup
          className="mt-3 space-y-1.5 sm:ml-8"
          disabled={isDisabled}
          onValueChange={(value) => {
            setOrdinal(question.id, Number(value));
            if (!answered) onFirstAnswer(question.id);
          }}
          value={ordinalAnswer?.toString() ?? ""}
        >
          {sortedChoices.map((choice) => (
            <Label
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-sm font-normal transition-colors",
                ordinalAnswer === choice.id
                  ? "border-gray-400 bg-gray-50 text-gray-900 dark:border-slate-500 dark:bg-slate-700/50 dark:text-white"
                  : "border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-slate-700 dark:text-gray-400 dark:hover:bg-slate-700/40",
              )}
              htmlFor={`q${question.id}-c${choice.id}`}
              key={choice.id}
            >
              <RadioGroupItem
                id={`q${question.id}-c${choice.id}`}
                value={choice.id.toString()}
              />
              <span className="flex-1">{choice.text}</span>
            </Label>
          ))}
        </RadioGroup>
      </div>
    );
  }

  if (question.type === "yes_no") {
    // A radio group, the same control the ordinal answers above use — two
    // answer styles in one form was the accidental part.
    //
    // Explicitly *not* a switch: a switch only writes state when it is
    // toggled, so a question whose honest answer is "No" stayed unanswered and
    // silently blocked completion until the coach toggled it twice (work item
    // 14). Picking "No" here is a selection, and it records `false`.
    const answered = yesNoAnswer !== undefined;
    // "" is unanswered — neither option selected, which is not the same as No.
    const selected = answered ? (yesNoAnswer ? "yes" : "no") : "";

    return (
      <div className={cardClass} ref={registerRef}>
        {header}
        <RadioGroup
          aria-label={question.text}
          className="mt-3 flex gap-2 sm:ml-8"
          disabled={isDisabled}
          onValueChange={(value) => {
            setYesNo(question.id, value === "yes");
            if (!answered) onFirstAnswer(question.id);
          }}
          value={selected}
        >
          {[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" },
          ].map((option) => (
            <Label
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-lg border px-4 py-2 text-sm font-normal transition-colors",
                selected === option.value
                  ? "border-gray-400 bg-gray-50 text-gray-900 dark:border-slate-500 dark:bg-slate-700/50 dark:text-white"
                  : "border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-slate-700 dark:text-gray-400 dark:hover:bg-slate-700/40",
              )}
              htmlFor={`q${question.id}-${option.value}`}
              key={option.value}
            >
              <RadioGroupItem
                id={`q${question.id}-${option.value}`}
                value={option.value}
              />
              <span>{option.label}</span>
            </Label>
          ))}
        </RadioGroup>
      </div>
    );
  }

  const inputId = `q-text-${question.id}`;
  return (
    <div className={cardClass} ref={registerRef}>
      {header}
      <div className="mt-3 sm:ml-8">
        <Label className="sr-only" htmlFor={inputId}>
          {question.text}
        </Label>
        {question.type === "multiline_text" ? (
          <Textarea
            disabled={isDisabled}
            id={inputId}
            onChange={(e) => setText(question.id, e.target.value)}
            rows={4}
            value={textAnswer}
          />
        ) : (
          <Input
            disabled={isDisabled}
            id={inputId}
            onChange={(e) => setText(question.id, e.target.value)}
            value={textAnswer}
          />
        )}
      </div>
    </div>
  );
};

export default QuestionField;
